/**
 * Atmosphere for the high camera (GDD §3.8): drifting cloud sprites between camera and ground, cloud shadows
 * sliding over the terrain, low river mist, and flocks of seven birds with flapping wings. Ported verbatim
 * (concept L764–L774, L1217–L1262), drawing from the world-generation stream in the concept's order.
 */
import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Sprite,
  SpriteMaterial,
  type Scene,
  type Texture,
} from 'three';
import { PERIOD } from '../core/math.ts';
import type { Rng } from '../core/rng.ts';
import type { FrameState } from '../render/frame.ts';
import type { WorldProfile } from './profiles.ts';

const TAU = Math.PI * 2;

export interface Flock {
  g: Group;
  members: { l: Mesh; r: Mesh; ph: number }[];
  speed: number;
}

export interface Atmosphere {
  clouds: Sprite[];
  birds: Flock[];
  cshadows: Mesh[];
  mists: Sprite[];
  apply(frame: FrameState): void;
  /**
   * Live animation (concept L1249–L1257, L1463): flocks fly and flap and re-place once past; cloud shadows slide
   * and respawn ahead; mist drifts downstream; clouds drift and wrap around the jet. `rand` is the effects stream.
   */
  step(dt: number, clock: number, jetZ: number, rand: Rng): void;
  /**
   * Floating-origin shift (concept L1258). Unlike the concept, clouds move too: the concept leaves them, so the
   * clouds near the jet vanish at every shift (master plan judgment call 15).
   */
  shiftZ(dz: number): void;
}

/** Clouds come straight after the rocks in the concept's stream (concept L766–L773). */
export function buildClouds(scene: Scene, W: WorldProfile, rand: Rng, cloudTex: Texture | null): Sprite[] {
  const clouds: Sprite[] = [];
  for (let ci = 0; ci < 18; ci++) {
    const cz = -rand() * PERIOD;
    const cx = W.center(cz) + (rand() - 0.5) * 260;
    const cy = 58 + rand() * 50;
    const csz = 70 + rand() * 90;
    const op = 0.07 + rand() * 0.1;
    for (const off of [0, -PERIOD]) {
      const sp = new Sprite(
        new SpriteMaterial({ map: cloudTex, transparent: true, depthWrite: false, opacity: op, fog: false }),
      );
      sp.scale.set(csz, csz * 0.7, 1);
      sp.position.set(cx, cy, cz + off);
      scene.add(sp);
      clouds.push(sp);
    }
  }
  return clouds;
}

/**
 * Birds, cloud shadows and mist (concept L1219–L1248), placed around the jet's start position `st`.
 * Called after the game objects, as in the concept; draws from the same stream right after the clouds.
 */
export function buildSkyLife(
  scene: Scene,
  W: WorldProfile,
  rand: Rng,
  cloudTex: Texture | null,
  st: { x: number; z: number },
  clouds: Sprite[],
): Atmosphere {
  const placeFlock = (fl: Flock, first: boolean, r: Rng = rand, jetZ = st.z) => {
    const z = jetZ - (first ? 40 + r() * 180 : 170 + r() * 120);
    const c = W.center(z);
    const side = r() < 0.5 ? 1 : -1;
    fl.g.position.set(c - side * (100 + r() * 50), 30 + r() * 12, z);
    fl.g.rotation.y = side > 0 ? -Math.PI / 2 + (r() - 0.5) * 0.6 : Math.PI / 2 + (r() - 0.5) * 0.6;
  };

  const wingGeo = new BufferGeometry();
  wingGeo.setAttribute('position', new BufferAttribute(new Float32Array([0, 0, 0.35, 1.25, 0, -0.1, 0, 0, -0.45]), 3));
  wingGeo.computeVertexNormals();
  const birdMat = new MeshBasicMaterial({ color: 0x2a2a2a, side: DoubleSide });
  const birdBody = new BoxGeometry(0.28, 0.2, 1.0);
  const birds: Flock[] = [];
  for (let fi = 0; fi < 3; fi++) {
    const fl: Flock = { g: new Group(), members: [], speed: 11 + rand() * 4 };
    for (let bi = 0; bi < 7; bi++) {
      const bd = new Group();
      const kk = Math.ceil(bi / 2);
      const sgn = bi % 2 ? 1 : -1;
      bd.position.set(sgn * kk * 2.3, (rand() - 0.5) * 1.2, kk * 2.1);
      const wl = new Mesh(wingGeo, birdMat);
      const wr = new Mesh(wingGeo, birdMat);
      wr.scale.x = -1;
      bd.add(wl);
      bd.add(wr);
      bd.add(new Mesh(birdBody, birdMat));
      fl.g.add(bd);
      fl.members.push({ l: wl, r: wr, ph: rand() * TAU });
    }
    scene.add(fl.g);
    birds.push(fl);
    placeFlock(fl, true);
  }

  const csMat = new MeshBasicMaterial({
    map: cloudTex,
    color: 0x000000,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const cshadows: Mesh[] = [];
  for (let cs = 0; cs < 7; cs++) {
    const pl = new Mesh(new PlaneGeometry(1, 1), csMat);
    pl.rotation.x = -Math.PI / 2;
    const sx = 150 + rand() * 90;
    const sy = 110 + rand() * 60;
    pl.scale.set(sx, sy, 1);
    const px = st.x + (rand() - 0.5) * 300;
    const pz = st.z + 80 - rand() * 460;
    pl.position.set(px, 42, pz);
    scene.add(pl);
    cshadows.push(pl);
  }

  const mists: Sprite[] = [];
  for (let mi = 0; mi < 12; mi++) {
    const ms = new Sprite(
      new SpriteMaterial({
        map: cloudTex,
        transparent: true,
        depthWrite: false,
        opacity: 0.04 + rand() * 0.05,
        fog: false,
      }),
    );
    const mz = st.z + 60 - rand() * 400;
    const sx = 60 + rand() * 40;
    const sy = 30 + rand() * 20;
    ms.scale.set(sx, sy, 1);
    const mx = W.center(mz) + (rand() - 0.5) * 50;
    const my = 4 + rand() * 3;
    ms.position.set(mx, my, mz);
    scene.add(ms);
    mists.push(ms);
  }

  function apply(frame: FrameState): void {
    const a = frame.atmos;
    clouds.forEach((c, i) => {
      const s = a.clouds[i];
      c.visible = !!s;
      if (!s) return;
      c.position.fromArray(s.pos);
      c.scale.fromArray(s.scale);
      c.material.opacity = s.opacity;
    });
    birds.forEach((fl, i) => {
      const s = a.birds[i];
      fl.g.visible = !!s;
      if (!s) return;
      fl.g.position.fromArray(s.pos);
      fl.g.rotation.y = s.rotY;
      fl.members.forEach((m, j) => {
        const w = s.wings[j] ?? 0;
        m.l.rotation.z = w;
        m.r.rotation.z = -w;
      });
    });
    cshadows.forEach((c, i) => {
      const s = a.cshadows[i];
      c.visible = !!s;
      if (!s) return;
      c.position.fromArray(s.pos);
      c.scale.fromArray(s.scale);
    });
    mists.forEach((m, i) => {
      const s = a.mists[i];
      m.visible = !!s;
      if (!s) return;
      m.position.fromArray(s.pos);
      m.scale.fromArray(s.scale);
      m.material.opacity = s.opacity;
    });
  }

  function step(dt: number, clock: number, jetZ: number, r: Rng): void {
    for (const fl of birds) {
      const hd = fl.g.rotation.y;
      fl.g.position.x += -Math.sin(hd) * fl.speed * dt;
      fl.g.position.z += -Math.cos(hd) * fl.speed * dt;
      for (const b of fl.members) {
        const a = Math.sin(clock * 9 + b.ph) * 0.65;
        b.l.rotation.z = a;
        b.r.rotation.z = -a;
      }
      const gp = fl.g.position;
      if (Math.abs(gp.x - W.center(gp.z)) > 190 || gp.z > jetZ + 60) placeFlock(fl, false, r, jetZ);
    }
    for (const pl of cshadows) {
      pl.position.x += dt * 4;
      if (pl.position.z > jetZ + 150) {
        pl.position.z = jetZ - 280 - r() * 160;
        pl.position.x = W.center(pl.position.z) + (r() - 0.5) * 260;
      }
    }
    for (const ms of mists) {
      ms.position.z += dt * 1.6;
      if (ms.position.z > jetZ + 80) {
        ms.position.z = jetZ - 250 - r() * 120;
        ms.position.x = W.center(ms.position.z) + (r() - 0.5) * 50;
      }
    }
    for (const cl of clouds) {
      cl.position.x += dt * 1.5;
      if (cl.position.z > jetZ + 200) cl.position.z -= PERIOD;
      if (cl.position.z < jetZ - 900) cl.position.z += PERIOD;
    }
  }

  function shiftZ(dz: number): void {
    for (const fl of birds) fl.g.position.z += dz;
    for (const pl of cshadows) pl.position.z += dz;
    for (const ms of mists) ms.position.z += dz;
    for (const cl of clouds) cl.position.z += dz;
  }

  return { clouds, birds, cshadows, mists, apply, step, shiftZ };
}
